import type { ResultFilter } from '../calculators/calculator-result-utility'
import { EnergyType, Quadrant, WasteType } from '../calculators/EnergyFlow'
import {
  HybridHeatPumpExtraOutputField,
  hybridHeatPumpExtraOutputFields,
  hybridHeatPumpExtraOutputProperties,
} from '../calculators/providers/HybridHeatPumpProvider'
import { PurchasedEnergyType } from '../generated/graphql'

export enum EnergyBusResultKey {
  HeatFromEnvironment = 'heatFromEnvironment',
  WasteEnergy = 'wasteEnergy',
  CoolingFromEnergyBus = 'coolingFromEnergyBus',
  HeatFromEnergyBus = 'heatFromEnergyBus',
  HeatToEnvironment = 'heatToEnvironment',
}

export type ResultSubCategory = ResultFilter & {
  nameKey: string
  purchasedEnergyType?: PurchasedEnergyType
}

export type ResultCategory = {
  nameKey: string
  subCategories: ResultSubCategory[]
}

export type PurchasedEnergyResultSubCategory = ResultSubCategory & {
  purchasedEnergyType: PurchasedEnergyType
}

export const energyBusSourceHeatingSubCategory: ResultSubCategory = {
  nameKey: 'energyBus.sidebar.heatFromEnergyBus',
  quadrant: Quadrant.FromEnvironment,
  energyType: EnergyType.Heating,
  providerProperty: 'canUseEnergyBus',
  hybridHeatPumpExtraOutputKey:
    HybridHeatPumpExtraOutputField.EnergyBusSourceHeating,
}

export const energyBusSourceCoolingSubCategory: ResultSubCategory = {
  nameKey: 'energyBus.sidebar.coolingFromEnergyBus',
  quadrant: Quadrant.FromEnvironment,
  energyType: EnergyType.Cooling,
  providerProperty: 'canUseEnergyBus',
  hybridHeatPumpExtraOutputKey:
    HybridHeatPumpExtraOutputField.EnergyBusSourceCooling,
}

const purchasedHeatingSubCategory: PurchasedEnergyResultSubCategory = {
  nameKey: 'purchasedEnergyType.Heating',
  quadrant: Quadrant.Requirement,
  energyType: EnergyType.Heating,
  purchasedEnergyType: PurchasedEnergyType.Heating,
  providerProperty: 'isPurchasedEnergy',
}
const purchasedCoolingSubCategory: PurchasedEnergyResultSubCategory = {
  nameKey: 'purchasedEnergyType.Cooling',
  quadrant: Quadrant.Requirement,
  energyType: EnergyType.Cooling,
  purchasedEnergyType: PurchasedEnergyType.Cooling,
  providerProperty: 'isPurchasedEnergy',
}
export const purchasedElectricitySubCategory: PurchasedEnergyResultSubCategory =
  {
    nameKey: 'purchasedEnergyType.Electricity',
    quadrant: Quadrant.Requirement,
    energyType: EnergyType.Electricity,
    purchasedEnergyType: PurchasedEnergyType.Electricity,
    providerProperty: 'isPurchasedEnergy',
  }
const purchasedFuelSubCategory: PurchasedEnergyResultSubCategory = {
  nameKey: 'purchasedEnergyType.Fuel',
  quadrant: Quadrant.Requirement,
  purchasedEnergyType: PurchasedEnergyType.Fuel,
  providerProperty: 'isFuelUser',
}
export const purchasedEnergySubCategories: PurchasedEnergyResultSubCategory[] =
  [
    purchasedHeatingSubCategory,
    purchasedCoolingSubCategory,
    purchasedElectricitySubCategory,
    purchasedFuelSubCategory,
  ]

const broughtHeatingSubCategory: ResultSubCategory = {
  nameKey: 'purchasedEnergyType.Heating',
  quadrant: Quadrant.Requirement,
  energyType: EnergyType.Heating,
}
const broughtCoolingSubCategory: ResultSubCategory = {
  nameKey: 'purchasedEnergyType.Cooling',
  quadrant: Quadrant.Requirement,
  energyType: EnergyType.Cooling,
}
const broughtElectricitySubCategory: ResultSubCategory = {
  nameKey: 'purchasedEnergyType.Electricity',
  quadrant: Quadrant.Requirement,
  energyType: EnergyType.Electricity,
}
const broughtFuelSubCategory: ResultSubCategory = {
  nameKey: 'purchasedEnergyType.Fuel',
  quadrant: Quadrant.Requirement,
  providerProperty: 'isFuelUser',
}
const broughtEnergySubCategories: ResultSubCategory[] = [
  broughtHeatingSubCategory,
  broughtCoolingSubCategory,
  broughtElectricitySubCategory,
  broughtFuelSubCategory,
]

const localHeatingSubCategory: ResultSubCategory = {
  nameKey: 'localEnergyNameBy.energyType.heating',
  quadrant: Quadrant.Requirement,
  energyType: EnergyType.Heating,
  providerProperty: 'isLocalEnergy',
}
const localCoolingSubCategory: ResultSubCategory = {
  nameKey: 'localEnergyNameBy.energyType.cooling',
  quadrant: Quadrant.Requirement,
  energyType: EnergyType.Cooling,
  providerProperty: 'isLocalEnergy',
}
export const localElectricitySubCategory: ResultSubCategory = {
  nameKey: 'localEnergyNameBy.energyType.electricity',
  quadrant: Quadrant.Requirement,
  energyType: EnergyType.Electricity,
  providerProperty: 'isLocalEnergy',
}
const commonLocalEnergySubCategories = [
  localHeatingSubCategory,
  localCoolingSubCategory,
  localElectricitySubCategory,
]

const buildingLocalEnergySubCategories = [
  ...commonLocalEnergySubCategories,
  {
    ...energyBusSourceHeatingSubCategory,
    nameKey: 'resultCategory.localHeatingFromEnergyBus',
  },
]
const localGridLocalEnergySubCategories = commonLocalEnergySubCategories

export const environmentalHeatSubCategory: ResultSubCategory = {
  nameKey: 'selfSufficientEnergyName.environmentalHeat',
  quadrant: Quadrant.FromEnvironment,
  energyType: EnergyType.Heating,
  providerProperty: 'isEnvironmentalHeat',
  energyBusResultKey: EnergyBusResultKey.HeatFromEnvironment,
}
export const wasteEnergyBasedSubCategory: ResultSubCategory = {
  nameKey: 'selfSufficientEnergyName.wasteEnergyBased',
  quadrant: Quadrant.FromEnvironment,
  energyType: EnergyType.Heating,
  providerProperty: 'isWasteEnergyBased',
  energyBusResultKey: EnergyBusResultKey.WasteEnergy,
}
export const greenEnergySubCategory: ResultSubCategory = {
  nameKey: 'selfSufficientEnergyName.greenEnergy',
  quadrant: Quadrant.Provided,
  providerProperty: 'isGreenEnergy',
}
const collectedGreenEnergySubCategory: ResultSubCategory = {
  nameKey: 'selfSufficientEnergyName.collectedGreenEnergy',
  quadrant: Quadrant.FromEnvironment,
  providerProperty: 'isGreenEnergy',
}
const usedGreenEnergySubCategory: ResultSubCategory = {
  nameKey: 'selfSufficientEnergyName.usedGreenEnergy',
  quadrant: Quadrant.Provided,
  providerProperty: 'isGreenEnergy',
}

export const selfSufficientEnergySubCategories: ResultSubCategory[] = [
  greenEnergySubCategory,
  environmentalHeatSubCategory,
  wasteEnergyBasedSubCategory,
]

const providerSelfSufficientEnergySubCategories: ResultSubCategory[] = [
  collectedGreenEnergySubCategory,
  usedGreenEnergySubCategory,
  environmentalHeatSubCategory,
  wasteEnergyBasedSubCategory,
]

export const netHeatingSubCategory = {
  nameKey: 'energyType.heating',
  energyType: EnergyType.Heating,
  isDemandBased: true,
}
export const netCoolingSubCategory = {
  nameKey: 'energyType.cooling',
  energyType: EnergyType.Cooling,
  isDemandBased: true,
}
export const netElectricitySubCategory = {
  nameKey: 'energyType.electricity',
  energyType: EnergyType.Electricity,
  isDemandBased: true,
}

const netEnergySubCategories: ResultSubCategory[] = [
  netHeatingSubCategory,
  netCoolingSubCategory,
  netElectricitySubCategory,
]
export const mapWithTransferredDemands = (
  subCategory: ResultSubCategory,
): ResultSubCategory => ({
  ...subCategory,
  includeTransferredDemands: true,
})
export const versionNetEnergySubCategories = netEnergySubCategories
export const buildingNetEnergySubCategories = netEnergySubCategories
export const localGridNetEnergySubCategories = netEnergySubCategories.map(
  mapWithTransferredDemands,
)

export const producedEnergySubCategories: ResultSubCategory[] = [
  {
    nameKey: 'energyType.heating',
    quadrant: Quadrant.Provided,
    energyType: EnergyType.Heating,
  },
  {
    nameKey: 'energyType.cooling',
    quadrant: Quadrant.Provided,
    energyType: EnergyType.Cooling,
  },
  {
    nameKey: 'energyType.electricity',
    quadrant: Quadrant.Provided,
    energyType: EnergyType.Electricity,
  },
]

const wasteEnergySubCategories: ResultSubCategory[] = [
  {
    nameKey: 'energyType.heating',
    quadrant: Quadrant.Waste,
    energyType: EnergyType.Heating,
    wasteType: WasteType.Lost,
  },
  {
    nameKey: 'energyType.electricity',
    quadrant: Quadrant.Waste,
    energyType: EnergyType.Electricity,
    wasteType: WasteType.Lost,
  },
]

export const soldElectricitySubCategory = {
  nameKey: 'energyType.electricity',
  quadrant: Quadrant.Waste,
  energyType: EnergyType.Electricity,
  wasteType: WasteType.Sold,
}

const soldEnergySubCategories: ResultSubCategory[] = [
  {
    nameKey: 'energyType.heating',
    quadrant: Quadrant.Waste,
    energyType: EnergyType.Heating,
    wasteType: WasteType.Sold,
  },
  soldElectricitySubCategory,
]

const purchasedEnergy: ResultCategory = {
  nameKey: 'resultCategory.purchasedEnergy',
  subCategories: purchasedEnergySubCategories,
}
const selfSufficientEnergy: ResultCategory = {
  nameKey: 'resultCategory.selfSufficientEnergy',
  subCategories: selfSufficientEnergySubCategories,
}
const totalNetEnergy: ResultCategory = {
  nameKey: 'resultCategory.netEnergy',
  subCategories: netEnergySubCategories,
}

export const versionResultCategories: ResultCategory[] = [
  purchasedEnergy,
  selfSufficientEnergy,
  totalNetEnergy,
]

export const buildingResultCategories: ResultCategory[] = [
  purchasedEnergy,
  {
    nameKey: 'resultCategory.localEnergy',
    subCategories: buildingLocalEnergySubCategories,
  },
  selfSufficientEnergy,
  {
    nameKey: 'resultCategory.netEnergy',
    subCategories: buildingNetEnergySubCategories,
  },
]

export const buildingResultCategoriesWithoutLocalEnergy: ResultCategory[] = [
  purchasedEnergy,
  selfSufficientEnergy,
  {
    nameKey: 'resultCategory.netEnergy',
    subCategories: buildingNetEnergySubCategories,
  },
]

export const localGridResultCategories: ResultCategory[] = [
  purchasedEnergy,
  {
    nameKey: 'resultCategory.localEnergy',
    subCategories: localGridLocalEnergySubCategories,
  },
  selfSufficientEnergy,
  {
    nameKey: 'resultCategory.netEnergy',
    subCategories: localGridNetEnergySubCategories,
  },
]

export const providerResultCategories: ResultCategory[] = [
  {
    nameKey: 'resultCategory.broughtEnergy',
    subCategories: broughtEnergySubCategories,
  },
  {
    nameKey: 'resultCategory.selfSufficientEnergy',
    subCategories: providerSelfSufficientEnergySubCategories,
  },
  {
    nameKey: 'resultCategory.producedEnergy',
    subCategories: producedEnergySubCategories,
  },
  {
    nameKey: 'resultCategory.lostEnergy',
    subCategories: wasteEnergySubCategories,
  },
  {
    nameKey: 'resultCategory.soldEnergy',
    subCategories: soldEnergySubCategories,
  },
]

const solarPanelFilter: ResultFilter = {
  energyType: EnergyType.Electricity,
  providerProperty: 'isSolarEnergy',
}

export const solarPanelSubCategories: ResultSubCategory[] = [
  {
    ...solarPanelFilter,
    nameKey: 'selfSufficientEnergyName.directUsedGreenEnergy',
    quadrant: Quadrant.Provided,
  },
  {
    ...solarPanelFilter,
    nameKey: 'selfSufficientEnergyName.batteryUsedGreenEnergy',
    quadrant: Quadrant.Waste,
    wasteType: WasteType.Recycled,
  },
  {
    ...solarPanelFilter,
    nameKey: 'report.lostElectricity',
    quadrant: Quadrant.Waste,
    wasteType: WasteType.Lost,
  },
  {
    ...solarPanelFilter,
    nameKey: 'report.soldElectricity',
    quadrant: Quadrant.Waste,
    wasteType: WasteType.Sold,
  },
  {
    ...solarPanelFilter,
    nameKey: 'selfSufficientEnergyName.collectedGreenEnergy',
    quadrant: Quadrant.FromEnvironment,
  },
]

export function mapHybridHeatPumpKeyToSubCategory(
  key: HybridHeatPumpExtraOutputField,
): ResultSubCategory {
  return {
    nameKey: `resultCategory.hybridHeatPump.${key}`,
    hybridHeatPumpExtraOutputKey: key,
  }
}

export const hybridHeatPumpResultSubCategories: ResultSubCategory[] =
  hybridHeatPumpExtraOutputFields
    .filter((key) => hybridHeatPumpExtraOutputProperties[key].isExported)
    .map(mapHybridHeatPumpKeyToSubCategory)

export const hybridHeatPumpResultCategories: ResultCategory[] = [
  ...providerResultCategories,
  {
    nameKey: 'resultCategory.hybridHeatPump.title',
    subCategories: hybridHeatPumpResultSubCategories,
  },
]
